<script>
import { mapGetters } from 'vuex';
import { useVuelidate } from '@vuelidate/core';
import { useAlert } from 'dashboard/composables';
import { required } from '@vuelidate/validators';
import router from '../../../../index';
import PageHeader from '../../SettingsSubPageHeader.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

// const shouldBeWebhookUrl = (value = '') =>
//   value ? value.startsWith('http://') || value.startsWith('https://') : true;

const phoneNumberValidator = (value = '') => {
  if (!value) return false;
  const trimmed = value.trim();
  // No debe contener espacios
  if (trimmed.includes(' ')) return false;
  // Debe contener solo números y el símbolo + al inicio
  if (!/^[+]?[0-9]+$/.test(trimmed)) return false;
  // Debe tener al menos 8 dígitos (sin contar el +)
  const numbersOnly = trimmed.replace(/^\+/, '');
  return numbersOnly.length >= 8;
};

export default {
  components: {
    PageHeader,
    NextButton,
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      phoneNumber: '',
      webhookUrl: '',
      apiUrl: '',
      apiKey: '',
      isLoading: false,
      // Estados para el flujo QR
      showQrStep: false,
      showConfigStep: false,
      qrCode: '',
      isGeneratingQr: false,
      connectionStatus: 'disconnected', // disconnected, connecting, connected
      evolutionInstance: null,
      connectionInterval: null,
      // Configuración de integración
      integrationConfig: {
        // OPCIONES TEMPORALMENTE DESHABILITADAS debido a errores en Evolution API
        importContacts: false,
        importMessages: false,
        daysLimitImportMessages: 1,
        conversationPending: false,
        // ÚNICA OPCIÓN FUNCIONAL: Mantiene el historial de conversaciones
        reopenConversation: true,
      },
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'inboxes/getUIFlags',
    }),
    autoWebhookUrl() {
      // Generar automáticamente la URL del webhook para EvolutionAPI
      const baseUrl = window.location.origin;
      const phone = this.phoneNumber?.trim();

      // Si no hay número, mostrar placeholder
      if (!phone) {
        return `${baseUrl}/chatwoot/webhook/[NUMERO_DE_TELEFONO]`;
      }

      // Validar que el número no contenga caracteres especiales problemáticos
      const cleanPhone = phone.replace(/[^0-9+]/g, '');

      return `${baseUrl}/chatwoot/webhook/${encodeURIComponent(cleanPhone)}`;
    },
    finalWebhookUrl() {
      return this.webhookUrl || this.autoWebhookUrl;
    },
  },
  watch: {
    // Actualizar la URL del webhook cuando cambie el número de teléfono
    phoneNumber() {
      // Siempre actualizar la URL cuando cambie el número
      this.webhookUrl = this.autoWebhookUrl;
    },
  },
  validations: {
    phoneNumber: { required, phoneNumberValidator },
    // webhookUrl: { required, shouldBeWebhookUrl }, // Oculto - se genera automáticamente
  },
  mounted() {
    this.loadGlobalConfig();
    // Inicializar con la URL auto-generada
    this.webhookUrl = this.autoWebhookUrl;
  },
  beforeUnmount() {
    // Limpiar polling al destruir el componente
    this.stopConnectionPolling();
  },
  methods: {
    // Método para limpiar el número de teléfono eliminando espacios y caracteres no válidos
    cleanPhoneNumber() {
      if (this.phoneNumber) {
        // Conservar solo números y el + al inicio
        let cleaned = this.phoneNumber.replace(/[^0-9+]/g, '');
        // Si hay un +, debe estar solo al inicio
        if (cleaned.includes('+')) {
          const parts = cleaned.split('+');
          cleaned = '+' + parts.join('');
        }
        this.phoneNumber = cleaned;
      }
    },

    loadGlobalConfig() {
      // Cargar configuraciones globales de Evolution API desde window.globalConfig
      try {
        const config = window.globalConfig || {};

        if (config.EVOLUTION_API_URL) {
          this.apiUrl = config.EVOLUTION_API_URL;
        } else {
          throw new Error('EVOLUTION_API_URL not configured');
        }

        if (config.EVOLUTION_API_KEY) {
          this.apiKey = config.EVOLUTION_API_KEY;
        } else {
          throw new Error('EVOLUTION_API_KEY not configured');
        }
      } catch (error) {
        // Si no hay configuraciones globales, mostrar error
        useAlert(
          'Evolution API no está configurado correctamente. Contacta al administrador.'
        );
      }
    },

    async createEvolutionInstance() {
      // Crear instancia básica en Evolution API
      const requestBody = {
        instanceName: this.phoneNumber,
        token: '',
        qrcode: true,
        integration: 'WHATSAPP-BAILEYS',
        // Configuraciones básicas mínimas
        reject_call: false,
        groupsIgnore: false,
        alwaysOnline: false,
        readMessages: false,
        readStatus: false,
        syncFullHistory: true,
      };

      const response = await fetch(`${this.apiUrl}/instance/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          apikey: this.apiKey,
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error(
          `Error creating Evolution API instance: ${response.statusText}`
        );
      }

      const result = await response.json();

      return result;
    },

    async createChannel() {
      this.v$.$touch();
      if (this.v$.$invalid) {
        return;
      }

      // Activar indicador de carga
      this.isLoading = true;

      // Validar que las configuraciones globales estén cargadas
      if (!this.apiUrl || !this.apiKey) {
        useAlert(
          'Evolution API no está configurado correctamente. Contacta al administrador.'
        );
        this.isLoading = false;
        return;
      }

      // Validación adicional para la URL del webhook
      const phone = this.phoneNumber?.trim();
      const cleanPhone = phone ? phone.replace(/[^0-9+]/g, '') : '';
      if (!phone || cleanPhone.length < 8) {
        useAlert(
          'Por favor, ingresa un número de teléfono válido (mínimo 8 dígitos).'
        );
        this.isLoading = false;
        return;
      }

      try {
        // Primero crear la instancia en Evolution API
        const evolutionInstance = await this.createEvolutionInstance();
        this.evolutionInstance = evolutionInstance;

        // Mostrar el paso del QR code
        this.showQrStep = true;

        // Generar QR code
        await this.generateQrCode();
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.EVOLUTION_API_CHANNEL.ERROR_MESSAGE'));
      } finally {
        // Desactivar indicador de carga
        this.isLoading = false;
      }
    },

    async generateQrCode() {
      this.isGeneratingQr = true;
      this.connectionStatus = 'connecting';

      try {
        const response = await fetch(
          `${this.apiUrl}/instance/connect/${this.phoneNumber}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              apikey: this.apiKey,
            },
          }
        );

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(
            `Error generating QR code: ${response.statusText} - ${errorText}`
          );
        }

        const result = await response.json();

        // Evolution API puede devolver diferentes formatos de QR
        let qrData = null;

        // Buscar QR en el formato que funciona según el código anterior
        if (result.qrcode) {
          qrData = result.qrcode.base64 || result.qrcode;
        } else if (result.base64) {
          qrData = result.base64;
        } else if (result.qr) {
          qrData = result.qr;
        } else if (result.code) {
          qrData = result.code;
        }

        if (qrData) {
          // Si el QR viene como base64 puro, agregar el prefijo data:image
          if (qrData && !qrData.startsWith('data:image/')) {
            qrData = `data:image/png;base64,${qrData}`;
          }

          this.qrCode = qrData;
          // Iniciar el polling para verificar conexión
          this.startConnectionPolling();
        } else {
          // Si no hay QR code en los campos esperados, mostrar toda la respuesta
          throw new Error(
            `QR code not found in response. Available fields: ${Object.keys(
              result
            ).join(', ')}`
          );
        }
      } catch (error) {
        useAlert(
          `Error al generar el código QR: ${error.message}. Por favor, intenta nuevamente.`
        );
        this.showQrStep = false;
      } finally {
        this.isGeneratingQr = false;
      }
    },

    async checkConnectionStatus() {
      // Verificar estado de conexión periódicamente
      try {
        const response = await fetch(
          `${this.apiUrl}/instance/connectionState/${this.phoneNumber}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              apikey: this.apiKey,
            },
          }
        );

        if (response.ok) {
          const result = await response.json();
          // Verificar múltiples formatos de estado como en el código anterior
          const isConnected =
            result.state === 'open' ||
            result.status === 'connected' ||
            result.instance?.state === 'open' ||
            result.instance?.status === 'connected';

          if (isConnected) {
            this.connectionStatus = 'connected';
            // Detener polling y mostrar la vista de configuración
            this.stopConnectionPolling();
            this.showQrStep = false;
            this.showConfigStep = true;
          }
        }
      } catch (error) {
        // Silencioso, continuar verificando
      }
    },

    async setupChatwootIntegration() {
      try {
        // Obtener datos del usuario actual desde el store
        const currentUser = this.$store.getters.getCurrentUser;
        const currentAccountId = this.$store.getters.getCurrentAccountId;

        // Configurar la integración de Chatwoot en EvolutionAPI
        const integrationData = {
          enabled: true,
          url: window.location.origin, // URL base de Chatwoot
          accountId:
            currentAccountId?.toString() || currentUser.account_id?.toString(),
          token: currentUser.access_token,
          signMsg: false,
          nameInbox: this.phoneNumber,
          reopenConversation: this.integrationConfig.reopenConversation,
          conversationPending: this.integrationConfig.conversationPending,
          autoCreate: true,
          // Configuraciones opcionales controladas por el usuario
          importContacts: this.integrationConfig.importContacts,
          importMessages: this.integrationConfig.importMessages,
          daysLimitImportMessages:
            this.integrationConfig.daysLimitImportMessages,
        };

        const response = await fetch(
          `${this.apiUrl}/chatwoot/set/${this.phoneNumber}`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              apikey: this.apiKey,
            },
            body: JSON.stringify(integrationData),
          }
        );

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(
            `Error setting up Chatwoot integration: ${response.status} - ${errorText}`
          );
        }

        const result = await response.json();

        // Log success
        return result;
      } catch (error) {
        // Log error for debugging
        // No mostramos error al usuario porque es opcional
        // La integración se puede configurar manualmente después
        return null;
      }
    },

    async handleIntegrationComplete() {
      // La integración está completa, EvolutionAPI ya creó el canal con autoCreate
      // Buscar el canal recién creado para redirigir a "add agents"
      useAlert(this.$t('INBOX_MGMT.EVOLUTION_API_CHANNEL.SUCCESS_MESSAGE'));

      // Mostrar mensaje indicando que estamos buscando el canal
      useAlert('Buscando el canal creado por Evolution API...');

      // Dar tiempo para que el canal se sincronice en Chatwoot
      setTimeout(async () => {
        try {
          // Buscar el canal recién creado por Evolution API
          const foundInboxId = await this.findRecentlyCreatedInbox();

          if (foundInboxId) {
            // Redirigir a agregar agentes del canal específico
            useAlert('Canal encontrado. Redirigiendo a agregar agentes...');
            router.replace({
              name: 'settings_inboxes_add_agents',
              params: {
                page: 'new',
                inbox_id: foundInboxId,
              },
            });
          } else {
            // Si no se encuentra el canal específico, ir a la lista de inboxes
            useAlert(
              'Canal no encontrado automáticamente. Ve a la lista de inboxes para configurar agentes.'
            );
            router.replace({
              name: 'settings_inboxes_page',
            });
          }
        } catch (error) {
          // En caso de error, ir a la lista de inboxes
          useAlert(
            'Error buscando el canal. Ve a la lista de inboxes para configurar agentes.'
          );
          router.replace({
            name: 'settings_inboxes_page',
          });
        }
      }, 3000); // Esperar 3 segundos para que Evolution API sincronice el canal
    },

    async createChatwootChannel() {
      try {
        // Crear el canal en Chatwoot como API channel con configuración Evolution API
        const channelData = {
          name: this.phoneNumber,
          channel: {
            type: 'api',
            webhook_url: this.finalWebhookUrl,
            additional_attributes: {
              api_provider: 'evolution_api',
              evolution_api_url: this.apiUrl,
              evolution_api_key: this.apiKey,
              evolution_instance_name: this.phoneNumber,
              evolution_instance_data: this.evolutionInstance,
              auto_generated_webhook: true,
            },
          },
        };

        const apiChannel = await this.$store.dispatch(
          'inboxes/createChannel',
          channelData
        );

        router.replace({
          name: 'settings_inboxes_add_agents',
          params: {
            page: 'new',
            inbox_id: apiChannel.id,
          },
        });

        useAlert(this.$t('INBOX_MGMT.EVOLUTION_API_CHANNEL.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.EVOLUTION_API_CHANNEL.ERROR_MESSAGE'));
      }
    },

    startConnectionPolling() {
      // Verificar conexión cada 5 segundos como en el código que funcionaba
      this.connectionInterval = setInterval(() => {
        if (this.connectionStatus === 'connecting') {
          this.checkConnectionStatus();
        }
      }, 5000);
    },

    stopConnectionPolling() {
      if (this.connectionInterval) {
        clearInterval(this.connectionInterval);
        this.connectionInterval = null;
      }
    },

    goBackToForm() {
      this.showQrStep = false;
      this.showConfigStep = false;
      this.qrCode = '';
      this.connectionStatus = 'disconnected';
      this.stopConnectionPolling();
    },

    onQrImageError() {
      useAlert(
        'Error al cargar la imagen del QR code. El formato puede ser incorrecto.'
      );
    },

    onQrImageLoad() {
      // QR code cargado correctamente
    },

    goBackToQr() {
      this.showConfigStep = false;
      this.showQrStep = true;
    },

    async completeIntegration() {
      this.isLoading = true;

      try {
        // Configurar la integración con los parámetros del usuario - SETUPCHATWOOTINTEGRATION
        await this.setupChatwootIntegration();

        // Completar la integración
        this.handleIntegrationComplete();
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.EVOLUTION_API_CHANNEL.ERROR_MESSAGE'));
      } finally {
        this.isLoading = false;
      }
    },

    async findRecentlyCreatedInbox() {
      // Buscar el canal recién creado por nombre/número de teléfono
      // como fallback cuando Evolution API no retorna inbox_id
      try {
        // Actualizar la lista de inboxes desde el servidor
        await this.$store.dispatch('inboxes/get');
        const inboxes = this.$store.getters['inboxes/getInboxes'];

        // Buscar inbox que coincida con el número de teléfono
        const recentInbox = inboxes.find(
          inbox =>
            inbox.name === this.phoneNumber ||
            inbox.phone_number === this.phoneNumber ||
            (inbox.additional_attributes &&
              inbox.additional_attributes.evolution_instance_name ===
                this.phoneNumber) ||
            (inbox.additional_attributes &&
              inbox.additional_attributes.api_provider === 'evolution_api' &&
              inbox.name.includes(this.phoneNumber))
        );

        if (recentInbox) {
          return recentInbox.id;
        }

        return null;
      } catch (error) {
        return null;
      }
    },
  },
};
</script>

<template>
  <div
    class="border border-n-weak bg-n-solid-1 rounded-t-lg border-b-0 h-full w-full p-6 col-span-6 overflow-auto"
  >
    <!-- Vista del formulario inicial -->
    <div v-if="!showQrStep && !showConfigStep">
      <PageHeader
        :header-title="$t('INBOX_MGMT.EVOLUTION_API_CHANNEL.TITLE')"
        :header-content="$t('INBOX_MGMT.EVOLUTION_API_CHANNEL.DESC')"
      />
      <form
        class="flex flex-wrap flex-col mx-0"
        :class="{ 'opacity-50 pointer-events-none': isLoading }"
        @submit.prevent="createChannel()"
      >
        <div class="flex-shrink-0 flex-grow-0">
          <label :class="{ error: v$.phoneNumber.$error }">
            <div class="flex items-center space-x-2 mb-2">
              <span class="font-medium text-n-700">
                {{ $t('INBOX_MGMT.EVOLUTION_API_CHANNEL.PHONE_NUMBER.LABEL') }}
              </span>
              <span
                class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800"
              >
                <svg
                  class="w-3 h-3 mr-1"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fill-rule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                    clip-rule="evenodd"
                  />
                </svg>
                {{
                  $t(
                    'INBOX_MGMT.EVOLUTION_API_CHANNEL.PHONE_NUMBER.COUNTRY_CODE_REQUIRED'
                  )
                }}
              </span>
            </div>
            <input
              v-model="phoneNumber"
              type="text"
              :disabled="isLoading"
              :placeholder="
                $t('INBOX_MGMT.EVOLUTION_API_CHANNEL.PHONE_NUMBER.PLACEHOLDER')
              "
              @input="cleanPhoneNumber"
              @blur="v$.phoneNumber.$touch"
            />
            <span v-if="v$.phoneNumber.$error" class="message">{{
              $t('INBOX_MGMT.EVOLUTION_API_CHANNEL.PHONE_NUMBER.ERROR')
            }}</span>
          </label>
          <div class="mt-2">
            <p class="help-text text-sm text-n-600">
              {{ $t('INBOX_MGMT.EVOLUTION_API_CHANNEL.PHONE_NUMBER.SUBTITLE') }}
            </p>
            <div class="mt-1 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div class="flex items-start space-x-2">
                <svg
                  class="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fill-rule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                    clip-rule="evenodd"
                  />
                </svg>
                <div class="text-sm">
                  <p class="font-medium text-blue-800 mb-1">
                    {{
                      $t(
                        'INBOX_MGMT.EVOLUTION_API_CHANNEL.PHONE_NUMBER.IMPORTANT'
                      )
                    }}
                  </p>
                  <p class="text-blue-700">
                    {{
                      $t(
                        'INBOX_MGMT.EVOLUTION_API_CHANNEL.PHONE_NUMBER.EXAMPLES'
                      )
                    }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Webhook URL oculto para el cliente - se genera automáticamente -->
        <!-- 
        <div class="flex-shrink-0 flex-grow-0">
          <label :class="{ error: v$.webhookUrl.$error }">
            {{ $t('INBOX_MGMT.EVOLUTION_API_CHANNEL.WEBHOOK_URL.LABEL') }}
            <input
              v-model="webhookUrl"
              type="text"
              readonly
              class="bg-gray-100 cursor-not-allowed"
              :placeholder="autoWebhookUrl"
            />
            <span v-if="v$.webhookUrl.$error" class="message">
              {{ $t('INBOX_MGMT.API_CHANNEL.WEBHOOK_URL.ERROR') }}
            </span>
          </label>
          <p class="help-text">
            {{ $t('INBOX_MGMT.EVOLUTION_API_CHANNEL.WEBHOOK_URL.SUBTITLE') }}
            <br />
            <small class="text-gray-500">
              {{
                $t(
                  'INBOX_MGMT.EVOLUTION_API_CHANNEL.WEBHOOK_URL.READ_ONLY_HELP'
                )
              }}
            </small>
          </p>
        </div>
        -->

        <div class="w-full mt-4">
          <NextButton
            :is-loading="uiFlags.isCreating || isLoading"
            :disabled="isLoading"
            type="submit"
            solid
            blue
            :label="
              isLoading
                ? $t('INBOX_MGMT.EVOLUTION_API_CHANNEL.CREATING_INSTANCE')
                : $t('INBOX_MGMT.EVOLUTION_API_CHANNEL.SUBMIT_BUTTON')
            "
          />
        </div>
      </form>
    </div>

    <!-- Vista del QR Code -->
    <div v-else-if="showQrStep && !showConfigStep" class="text-center">
      <PageHeader
        :header-title="$t('INBOX_MGMT.EVOLUTION_API_CHANNEL.QR_CODE.TITLE')"
        :header-content="
          $t('INBOX_MGMT.EVOLUTION_API_CHANNEL.QR_CODE.SUBTITLE')
        "
      />

      <!-- Estado: Generando QR -->
      <div v-if="isGeneratingQr" class="py-8">
        <div
          class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"
        />
        <p class="text-gray-600">
          {{ $t('INBOX_MGMT.EVOLUTION_API_CHANNEL.QR_CODE.GENERATING') }}
        </p>
      </div>

      <!-- Estado: QR Code listo -->
      <div v-else-if="qrCode && connectionStatus === 'connecting'" class="py-8">
        <div class="bg-white p-8 rounded-lg shadow-lg inline-block mb-6">
          <img
            :src="qrCode"
            alt="WhatsApp QR Code"
            class="w-64 h-64 mx-auto"
            @error="onQrImageError"
            @load="onQrImageLoad"
          />
        </div>
        <div class="mb-4">
          <div class="animate-pulse">
            <div class="flex items-center justify-center space-x-2">
              <div class="w-2 h-2 bg-blue-600 rounded-full animate-bounce" />
              <div
                class="w-2 h-2 bg-blue-600 rounded-full animate-bounce delay-100"
              />
              <div
                class="w-2 h-2 bg-blue-600 rounded-full animate-bounce delay-200"
              />
            </div>
          </div>
          <p class="text-gray-600 mt-2">
            {{ $t('INBOX_MGMT.EVOLUTION_API_CHANNEL.QR_CODE.WAITING') }}
          </p>
        </div>
        <div
          class="bg-blue-50 border border-blue-200 rounded-lg p-4 max-w-md mx-auto"
        >
          <p class="text-sm text-blue-800">
            {{ $t('INBOX_MGMT.EVOLUTION_API_CHANNEL.QR_CODE.INSTRUCTIONS') }}
          </p>
        </div>
      </div>

      <!-- Estado: Conectado -->
      <div v-else-if="connectionStatus === 'connected'" class="py-8">
        <div class="text-green-600 mb-4">
          <svg
            class="w-16 h-16 mx-auto mb-4"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fill-rule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
              clip-rule="evenodd"
            />
          </svg>
        </div>
        <h3 class="text-xl font-semibold text-green-600 mb-2">
          {{ $t('INBOX_MGMT.EVOLUTION_API_CHANNEL.QR_CODE.SUCCESS') }}
        </h3>
        <p class="text-gray-600 mb-4">
          {{ $t('INBOX_MGMT.EVOLUTION_API_CHANNEL.QR_CODE.REDIRECTING') }}
        </p>
      </div>

      <!-- Botones de acción -->
      <div class="mt-8 space-x-4">
        <button
          type="button"
          class="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          @click="goBackToForm"
        >
          {{ $t('INBOX_MGMT.EVOLUTION_API_CHANNEL.BUTTONS.BACK_TO_START') }}
        </button>
        <button
          v-if="qrCode && connectionStatus === 'connecting'"
          type="button"
          class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          @click="generateQrCode"
        >
          {{ $t('INBOX_MGMT.EVOLUTION_API_CHANNEL.BUTTONS.REFRESH_QR') }}
        </button>
      </div>
    </div>

    <!-- Vista de configuración de integración -->
    <div v-else-if="showConfigStep" class="max-w-4xl mx-auto">
      <PageHeader
        :header-title="
          $t('INBOX_MGMT.EVOLUTION_API_CHANNEL.CONFIGURATION.TITLE')
        "
        :header-content="
          $t('INBOX_MGMT.EVOLUTION_API_CHANNEL.CONFIGURATION.SUBTITLE')
        "
      />

      <div class="border border-n-weak bg-n-solid-1 rounded-lg shadow">
        <!-- Header -->
        <div class="bg-woot-500 px-6 py-4 rounded-t-lg">
          <h3 class="text-xl font-semibold text-white">
            {{ $t('INBOX_MGMT.EVOLUTION_API_CHANNEL.CONFIGURATION.HEADER') }}
          </h3>
          <p class="text-woot-100 text-sm">
            {{ $t('INBOX_MGMT.EVOLUTION_API_CHANNEL.CONFIGURATION.DESC') }}
          </p>
        </div>

        <div class="p-6 space-y-6">
          <!-- Importar Contactos -->
          <!-- Import Contacts -->
          <!-- TEMPORALMENTE OCULTO: Esta funcionalidad presenta errores con la versión actual de Evolution API -->
          <div
            v-if="false"
            class="bg-n-solid-2 rounded-lg p-4 border-l-4 border-woot-500"
          >
            <div class="flex items-center justify-between">
              <div class="flex-1">
                <h4 class="text-lg font-medium text-n-800 mb-1">
                  {{
                    $t(
                      'INBOX_MGMT.EVOLUTION_API_CHANNEL.CONFIGURATION.IMPORT_CONTACTS.TITLE'
                    )
                  }}
                </h4>
                <p class="text-sm text-n-600">
                  {{
                    $t(
                      'INBOX_MGMT.EVOLUTION_API_CHANNEL.CONFIGURATION.IMPORT_CONTACTS.DESC'
                    )
                  }}
                </p>
              </div>
              <div class="ml-4">
                <label class="relative inline-flex items-center cursor-pointer">
                  <input
                    v-model="integrationConfig.importContacts"
                    type="checkbox"
                    class="sr-only peer"
                  />
                  <div
                    class="relative w-11 h-6 bg-n-200 rounded-full peer peer-checked:bg-woot-500 peer-focus:ring-4 peer-focus:ring-woot-300 after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full"
                  />
                </label>
              </div>
            </div>
          </div>

          <!-- Importar Mensajes -->
          <!-- TEMPORALMENTE OCULTO: Esta funcionalidad presenta errores con la versión actual de Evolution API -->
          <div
            v-if="false"
            class="bg-n-solid-2 rounded-lg p-4 border-l-4 border-woot-500"
          >
            <div class="flex items-center justify-between">
              <div class="flex-1">
                <h4 class="text-lg font-medium text-n-800 mb-1">
                  {{
                    $t(
                      'INBOX_MGMT.EVOLUTION_API_CHANNEL.CONFIGURATION.IMPORT_MESSAGES.TITLE'
                    )
                  }}
                </h4>
                <p class="text-sm text-n-600">
                  {{
                    $t(
                      'INBOX_MGMT.EVOLUTION_API_CHANNEL.CONFIGURATION.IMPORT_MESSAGES.DESC'
                    )
                  }}
                </p>
              </div>
              <div class="ml-4">
                <label class="relative inline-flex items-center cursor-pointer">
                  <input
                    v-model="integrationConfig.importMessages"
                    type="checkbox"
                    class="sr-only peer"
                  />
                  <div
                    class="relative w-11 h-6 bg-n-200 rounded-full peer peer-checked:bg-woot-500 peer-focus:ring-4 peer-focus:ring-woot-300 after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full"
                  />
                </label>
              </div>
            </div>

            <!-- Configuración de días límite -->
            <div
              v-if="integrationConfig.importMessages"
              class="mt-4 pl-4 border-l-2 border-woot-200 bg-n-solid-1 rounded p-3"
            >
              <label class="block text-sm font-medium text-n-700 mb-2">
                {{
                  $t(
                    'INBOX_MGMT.EVOLUTION_API_CHANNEL.CONFIGURATION.DAYS_LIMIT.LABEL'
                  )
                }}
              </label>
              <div class="flex items-center space-x-3">
                <input
                  v-model.number="integrationConfig.daysLimitImportMessages"
                  type="number"
                  min="0"
                  max="30"
                  class="w-20 rounded border-n-300 bg-n-solid-1 text-n-900 shadow-sm focus:border-woot-500 focus:ring-woot-500 text-sm"
                />
                <span class="text-sm text-n-600 font-medium">
                  {{
                    $t(
                      'INBOX_MGMT.EVOLUTION_API_CHANNEL.CONFIGURATION.DAYS_LIMIT.HELP'
                    )
                  }}
                </span>
              </div>
              <p class="mt-2 text-xs text-n-500">
                {{
                  $t(
                    'INBOX_MGMT.EVOLUTION_API_CHANNEL.CONFIGURATION.DAYS_LIMIT.NOTE'
                  )
                }}
              </p>
            </div>
          </div>

          <!-- Conversation Pending -->
          <!-- TEMPORALMENTE OCULTO: Solo se muestra la opción de Reopen Conversation que es la que funciona -->
          <div
            v-if="false"
            class="bg-n-solid-2 rounded-lg p-4 border-l-4 border-woot-500"
          >
            <div class="flex items-center justify-between">
              <div class="flex-1">
                <h4 class="text-lg font-medium text-n-800 mb-1">
                  {{
                    $t(
                      'INBOX_MGMT.EVOLUTION_API_CHANNEL.CONFIGURATION.CONVERSATION_PENDING.TITLE'
                    )
                  }}
                </h4>
                <p class="text-sm text-n-600">
                  {{
                    $t(
                      'INBOX_MGMT.EVOLUTION_API_CHANNEL.CONFIGURATION.CONVERSATION_PENDING.DESC'
                    )
                  }}
                </p>
              </div>
              <div class="ml-4">
                <label class="relative inline-flex items-center cursor-pointer">
                  <input
                    v-model="integrationConfig.conversationPending"
                    type="checkbox"
                    class="sr-only peer"
                  />
                  <div
                    class="relative w-11 h-6 bg-n-200 rounded-full peer peer-checked:bg-woot-500 peer-focus:ring-4 peer-focus:ring-woot-300 after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full"
                  />
                </label>
              </div>
            </div>
          </div>

          <!-- Reopen Conversation -->
          <!-- FUNCIONAL: Esta es la única opción que funciona correctamente con Evolution API actual -->
          <div class="bg-n-solid-2 rounded-lg p-4 border-l-4 border-woot-500">
            <div class="flex items-center justify-between">
              <div class="flex-1">
                <h4 class="text-lg font-medium text-n-800 mb-1">
                  {{
                    $t(
                      'INBOX_MGMT.EVOLUTION_API_CHANNEL.CONFIGURATION.REOPEN_CONVERSATION.TITLE'
                    )
                  }}
                </h4>
                <p class="text-sm text-n-600">
                  {{
                    $t(
                      'INBOX_MGMT.EVOLUTION_API_CHANNEL.CONFIGURATION.REOPEN_CONVERSATION.DESC'
                    )
                  }}
                </p>
              </div>
              <div class="ml-4">
                <label class="relative inline-flex items-center cursor-pointer">
                  <input
                    v-model="integrationConfig.reopenConversation"
                    type="checkbox"
                    class="sr-only peer"
                  />
                  <div
                    class="relative w-11 h-6 bg-n-200 rounded-full peer peer-checked:bg-woot-500 peer-focus:ring-4 peer-focus:ring-woot-300 after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full"
                  />
                </label>
              </div>
            </div>
          </div>

          <!-- Resumen -->
          <div class="bg-woot-100 rounded-lg p-4 border border-woot-300">
            <h4 class="text-sm font-semibold text-woot-900 mb-3">
              {{
                $t(
                  'INBOX_MGMT.EVOLUTION_API_CHANNEL.CONFIGURATION.SUMMARY.TITLE'
                )
              }}
            </h4>
            <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
              <!-- OPCIONES TEMPORALMENTE OCULTAS: Contactos, Mensajes y Días Límite -->
              <!-- SOLO SE MUESTRA: Reopen Conversation (la única funcional) -->
              <div
                v-if="false"
                class="bg-n-solid-1 rounded p-2 border border-n-200"
              >
                <div class="flex items-center justify-between text-xs">
                  <span class="font-medium text-n-600">
                    {{
                      $t(
                        'INBOX_MGMT.EVOLUTION_API_CHANNEL.CONFIGURATION.SUMMARY.CONTACTS'
                      )
                    }}
                  </span>
                  <span
                    class="px-2 py-1 rounded-full font-medium"
                    :class="
                      integrationConfig.importContacts
                        ? 'bg-woot-100 text-woot-800'
                        : 'bg-n-100 text-n-800'
                    "
                  >
                    {{
                      integrationConfig.importContacts
                        ? $t(
                            'INBOX_MGMT.EVOLUTION_API_CHANNEL.CONFIGURATION.VALUES.YES'
                          )
                        : $t(
                            'INBOX_MGMT.EVOLUTION_API_CHANNEL.CONFIGURATION.VALUES.NO'
                          )
                    }}
                  </span>
                </div>
              </div>
              <div
                v-if="false"
                class="bg-n-solid-1 rounded p-2 border border-n-200"
              >
                <div class="flex items-center justify-between text-xs">
                  <span class="font-medium text-n-600">
                    {{
                      $t(
                        'INBOX_MGMT.EVOLUTION_API_CHANNEL.CONFIGURATION.SUMMARY.MESSAGES'
                      )
                    }}
                  </span>
                  <span
                    class="px-2 py-1 rounded-full font-medium"
                    :class="
                      integrationConfig.importMessages
                        ? 'bg-woot-100 text-woot-800'
                        : 'bg-n-100 text-n-800'
                    "
                  >
                    {{
                      integrationConfig.importMessages
                        ? $t(
                            'INBOX_MGMT.EVOLUTION_API_CHANNEL.CONFIGURATION.VALUES.YES'
                          )
                        : $t(
                            'INBOX_MGMT.EVOLUTION_API_CHANNEL.CONFIGURATION.VALUES.NO'
                          )
                    }}
                  </span>
                </div>
              </div>
              <div
                v-if="false"
                class="bg-n-solid-1 rounded p-2 border border-n-200"
              >
                <div class="flex items-center justify-between text-xs">
                  <span class="font-medium text-n-600">
                    {{
                      $t(
                        'INBOX_MGMT.EVOLUTION_API_CHANNEL.CONFIGURATION.SUMMARY.DAYS_LIMIT'
                      )
                    }}
                  </span>
                  <span
                    class="px-2 py-1 rounded-full font-medium bg-woot-100 text-woot-800"
                  >
                    {{ integrationConfig.daysLimitImportMessages || 0 }}
                    {{
                      $t(
                        'INBOX_MGMT.EVOLUTION_API_CHANNEL.CONFIGURATION.DAYS_LIMIT.UNIT'
                      )
                    }}
                  </span>
                </div>
              </div>
              <div
                v-if="false"
                class="bg-n-solid-1 rounded p-2 border border-n-200"
              >
                <div class="flex items-center justify-between text-xs">
                  <span class="font-medium text-n-600">
                    {{
                      $t(
                        'INBOX_MGMT.EVOLUTION_API_CHANNEL.CONFIGURATION.SUMMARY.CONVERSATION_PENDING'
                      )
                    }}
                  </span>
                  <span
                    class="px-2 py-1 rounded-full font-medium"
                    :class="
                      integrationConfig.conversationPending
                        ? 'bg-woot-100 text-woot-800'
                        : 'bg-n-100 text-n-800'
                    "
                  >
                    {{
                      integrationConfig.conversationPending
                        ? $t(
                            'INBOX_MGMT.EVOLUTION_API_CHANNEL.CONFIGURATION.VALUES.YES'
                          )
                        : $t(
                            'INBOX_MGMT.EVOLUTION_API_CHANNEL.CONFIGURATION.VALUES.NO'
                          )
                    }}
                  </span>
                </div>
              </div>
              <div class="bg-n-solid-1 rounded p-2 border border-n-200">
                <div class="flex items-center justify-between text-xs">
                  <span class="font-medium text-n-600">
                    {{
                      $t(
                        'INBOX_MGMT.EVOLUTION_API_CHANNEL.CONFIGURATION.SUMMARY.REOPEN_CONVERSATION'
                      )
                    }}
                  </span>
                  <span
                    class="px-2 py-1 rounded-full font-medium"
                    :class="
                      integrationConfig.reopenConversation
                        ? 'bg-woot-100 text-woot-800'
                        : 'bg-n-100 text-n-800'
                    "
                  >
                    {{
                      integrationConfig.reopenConversation
                        ? $t(
                            'INBOX_MGMT.EVOLUTION_API_CHANNEL.CONFIGURATION.VALUES.YES'
                          )
                        : $t(
                            'INBOX_MGMT.EVOLUTION_API_CHANNEL.CONFIGURATION.VALUES.NO'
                          )
                    }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Botones -->
        <div
          class="bg-n-solid-2 px-6 py-4 flex items-center justify-between border-t border-n-weak rounded-b-lg"
        >
          <button
            type="button"
            class="inline-flex items-center px-4 py-2 border border-n-300 rounded shadow-sm text-sm font-medium text-n-700 bg-n-solid-1 hover:bg-n-solid-2 focus:outline-none focus:ring-2 focus:ring-woot-500"
            @click="goBackToQr"
          >
            {{ $t('INBOX_MGMT.EVOLUTION_API_CHANNEL.BUTTONS.BACK_TO_QR') }}
          </button>
          <NextButton
            :is-loading="isLoading"
            solid
            blue
            :label="
              $t(
                'INBOX_MGMT.EVOLUTION_API_CHANNEL.BUTTONS.COMPLETE_INTEGRATION'
              )
            "
            @click="completeIntegration"
          />
        </div>
      </div>
    </div>
  </div>
</template>
